import { useEffect, useRef, useState } from 'react';

import { exportCSV, exportSelected } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { HIDEABLE_COLUMNS } from '../utils/columnPrefs.js';
import { ASSIGNEES } from './AssigneeDropdown.jsx';
import ColumnToggle from './ColumnToggle.jsx';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'pending_complete', label: 'Pending Complete' },
  { value: 'resolved', label: 'Resolved' },
  // Rendered under a divider as a distinct "no work" state. It is included
  // in the default "All statuses" view; this option filters to only these.
  { value: 'no_action_needed', label: 'No Action Needed' },
];

// Sentinel value the dropdown emits when the user picks the "show system
// statuses" option; we translate it into include_system=true in onChange.
const SHOW_SYSTEM_SENTINEL = '__system__';

const SEVERITY_OPTIONS = [
  { value: '', label: 'All severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

// Sentinels for the assignee filter — translated to query params under
// the hood so the URL stays symmetric with named assignees.
const UNASSIGNED_SENTINEL = '__unassigned__';
const CUSTOM_ASSIGNEE_SENTINEL = '__custom__';

export default function FilterBar({
  filters,
  onFiltersChange,
  selectedIds,
  onClearSelection,
  columnVisibility,
  onColumnVisibilityChange,
}) {
  const selectedCount = selectedIds ? selectedIds.size : 0;
  const { isAdmin } = useAuth();
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const searchTimer = useRef(null);

  // Debounce the search input by 300ms before pushing it to parent.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if ((filters.search || '') !== (searchInput || '')) {
        onFiltersChange({ ...filters, search: searchInput || undefined });
      }
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Reflect external resets back into the local input.
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  const hasActive =
    !!filters.status ||
    !!filters.severity ||
    !!filters.search ||
    !!filters.include_system ||
    !!filters.assigned_to_name ||
    !!filters.unassigned ||
    !!filters.custom_assignee;

  function update(patch) {
    onFiltersChange({ ...filters, ...patch });
  }

  function handleStatusChange(value) {
    if (value === SHOW_SYSTEM_SENTINEL) {
      // Select-all-including-system: turn the toggle on, clear any
      // explicit status filter.
      update({ status: undefined, include_system: true });
    } else {
      update({ status: value || undefined, include_system: undefined });
    }
  }

  function handleAssigneeFilterChange(value) {
    if (value === UNASSIGNED_SENTINEL) {
      update({
        assigned_to_name: undefined,
        unassigned: true,
        custom_assignee: undefined,
      });
    } else if (value === CUSTOM_ASSIGNEE_SENTINEL) {
      update({
        assigned_to_name: undefined,
        unassigned: undefined,
        custom_assignee: true,
      });
    } else {
      update({
        assigned_to_name: value || undefined,
        unassigned: undefined,
        custom_assignee: undefined,
      });
    }
  }

  const assigneeFilterValue = filters.unassigned
    ? UNASSIGNED_SENTINEL
    : filters.custom_assignee
    ? CUSTOM_ASSIGNEE_SENTINEL
    : filters.assigned_to_name || '';

  async function handleExport() {
    try {
      setExporting(true);
      setExportError(null);
      const {
        search, status, severity, include_system,
        assigned_to_name, unassigned, custom_assignee,
      } = filters;
      await exportCSV({
        search, status, severity, include_system,
        assigned_to_name, unassigned, custom_assignee,
      });
    } catch (err) {
      setExportError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  async function handleExportSelected() {
    if (!selectedIds || selectedIds.size === 0) return;
    try {
      setExporting(true);
      setExportError(null);
      await exportSelected(Array.from(selectedIds));
      onClearSelection?.();
    } catch (err) {
      setExportError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }


  const statusValue = filters.include_system
    ? SHOW_SYSTEM_SENTINEL
    : filters.status || '';

  return (
    <>
      <div className="filter-bar">
        <div className="filter-bar__search">
          <span className="filter-bar__search-icon" aria-hidden="true">
            <SearchIcon />
          </span>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, email, or issue..."
            aria-label="Search submissions"
          />
        </div>

        <select
          className="filter-bar__select"
          value={statusValue}
          onChange={(e) => handleStatusChange(e.target.value)}
          aria-label="Filter by status"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          <option disabled>──────────</option>
          <option value={SHOW_SYSTEM_SENTINEL}>Show system statuses</option>
        </select>

        <select
          className="filter-bar__select"
          value={filters.severity || ''}
          onChange={(e) => update({ severity: e.target.value || undefined })}
          aria-label="Filter by severity"
        >
          {SEVERITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          className="filter-bar__select"
          value={assigneeFilterValue}
          onChange={(e) => handleAssigneeFilterChange(e.target.value)}
          aria-label="Filter by assignee"
        >
          <option value="">All assignees</option>
          {ASSIGNEES.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
          <option value={UNASSIGNED_SENTINEL}>Unassigned</option>
          <option value={CUSTOM_ASSIGNEE_SENTINEL}>Other / Custom</option>
        </select>

        {hasActive && (
          <button
            type="button"
            className="filter-bar__clear"
            onClick={() =>
              onFiltersChange({
                status: undefined,
                severity: undefined,
                search: undefined,
                include_system: undefined,
                assigned_to_name: undefined,
                unassigned: undefined,
                custom_assignee: undefined,
              })
            }
          >
            Clear filters
          </button>
        )}

        {columnVisibility && onColumnVisibilityChange && (
          <ColumnToggle
            columns={HIDEABLE_COLUMNS}
            visibility={columnVisibility}
            onVisibilityChange={onColumnVisibilityChange}
          />
        )}

        {isAdmin() && (
          <div className="filter-bar__actions">
            {selectedCount > 0 && (
              <button
                type="button"
                className="btn btn--accent"
                onClick={handleExportSelected}
                disabled={exporting}
              >
                {exporting ? 'Exporting…' : `Export Selected (${selectedCount})`}
              </button>
            )}
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        )}
      </div>

      {exportError && <div className="form-error">{exportError}</div>}
    </>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15.5 14h-.8l-.3-.3a6.5 6.5 0 10-.7.7l.3.3v.8l5 5 1.5-1.5-5-5zm-6 0a4.5 4.5 0 110-9 4.5 4.5 0 010 9z"
        fill="currentColor"
      />
    </svg>
  );
}

